import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { Button } from "./ui/button";
import { Sun, Moon, Compass, BookOpen, Target, Coins, TrendingUp, Shield, Sparkles, Menu, LogOut, User as UserIcon, Briefcase, FileText, Building2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

export const PILLARS = [
  { slug: "discover", name: "Discover", icon: Compass,   accent: "text-sky-400" },
  { slug: "learn",    name: "Learn",    icon: BookOpen,  accent: "text-primary" },
  { slug: "practice", name: "Practice", icon: Target,    accent: "text-emerald-400" },
  { slug: "earn",     name: "Earn",     icon: Coins,     accent: "text-amber-400" },
  { slug: "advance",  name: "Advance",  icon: TrendingUp,accent: "text-fuchsia-400" },
  { slug: "protect",  name: "Protect",  icon: Shield,    accent: "text-rose-400" },
];

export default function Header() {
  const { user, signout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const loc = useLocation();
  const initials = (user?.full_name || "M T").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const isActive = (slug) => loc.pathname.startsWith(`/app/${slug}`);

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 glass border-b border-border">
      <div className="max-w-[1480px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to={user ? "/app" : "/"} data-testid="brand-link" className="flex items-center gap-2.5 shrink-0">
          <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-mono text-xs font-bold">MT</span>
          </div>
          <div className="leading-none hidden sm:block">
            <div className="text-sm font-semibold tracking-tight">Executives</div>
            <div className="label-caps text-[9px] tracking-[0.28em] mt-0.5">Mahakali Tribunal</div>
          </div>
        </Link>

        {user && (
          <nav className="hidden lg:flex items-center gap-0.5">
            {PILLARS.map((p) => (
              <Link key={p.slug} to={`/app/${p.slug}`} data-testid={`nav-${p.slug}`}
                className={`px-2.5 py-1.5 rounded-md text-sm transition-colors duration-200 flex items-center gap-1.5 ${
                  isActive(p.slug) ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                }`}>
                <p.icon className={`h-3.5 w-3.5 ${isActive(p.slug) ? p.accent : ""}`} /> {p.name}
              </Link>
            ))}
            <Link to="/app/jobs" data-testid="nav-jobs"
              className={`px-2.5 py-1.5 rounded-md text-sm transition-colors duration-200 flex items-center gap-1.5 ${
                isActive("jobs") || isActive("roles") ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
              }`}>
              <Briefcase className="h-3.5 w-3.5" /> Jobs
            </Link>
            <Link to="/app/tribunal" data-testid="nav-tribunal"
              className={`ml-2 px-2.5 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors duration-200 ${
                loc.pathname.startsWith("/app/tribunal") ? "bg-primary/15 text-primary" : "text-primary/90 hover:text-primary"
              }`}>
              <Sparkles className="h-3.5 w-3.5" /> Tribunal
            </Link>
          </nav>
        )}

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme">
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>

          {user ? (
            <>
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-trigger">
                    <Menu className="h-4 w-4" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-72">
                  <div className="mt-8 flex flex-col gap-1">
                    <Link to="/app" className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-secondary text-sm">
                      <Sparkles className="h-4 w-4 text-primary" /> Dashboard
                    </Link>
                    {PILLARS.map((p) => (
                      <Link key={p.slug} to={`/app/${p.slug}`} data-testid={`m-nav-${p.slug}`}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-secondary text-sm">
                        <p.icon className={`h-4 w-4 ${p.accent}`} /> {p.name}
                      </Link>
                    ))}
                    <Link to="/app/jobs" data-testid="m-nav-jobs" className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-secondary text-sm">
                      <Briefcase className="h-4 w-4" /> Jobs
                    </Link>
                    <Link to="/app/studio" data-testid="m-nav-studio" className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-secondary text-sm">
                      <FileText className="h-4 w-4" /> Resume builder
                    </Link>
                    <Link to="/app/tribunal" className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-secondary text-sm">
                      <Sparkles className="h-4 w-4 text-primary" /> Tribunal Advisor
                    </Link>
                  </div>
                </SheetContent>
              </Sheet>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="rounded-full h-9 w-9 p-0" data-testid="user-menu">
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="bg-primary/10 text-primary text-xs font-mono">{initials}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="text-sm font-medium">{user.full_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => nav("/app/profile")} data-testid="menu-profile">
                    <UserIcon className="h-4 w-4 mr-2" /> Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/app/studio")} data-testid="menu-studio">
                    <FileText className="h-4 w-4 mr-2" /> Resume builder
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/app/orgs")} data-testid="menu-orgs">
                    <Building2 className="h-4 w-4 mr-2" /> My organizations
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/app/paths")} data-testid="menu-paths">
                    <BookOpen className="h-4 w-4 mr-2" /> Study paths
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/app/journal")} data-testid="menu-journal">
                    <Target className="h-4 w-4 mr-2" /> Decision journal
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/app/digest")} data-testid="menu-digest">
                    <Sparkles className="h-4 w-4 mr-2" /> Weekly digest
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { signout(); nav("/"); }} data-testid="menu-signout">
                    <LogOut className="h-4 w-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => nav("/auth")} data-testid="nav-signin" className="hidden sm:inline-flex">Sign in</Button>
              <Button onClick={() => nav("/auth?mode=signup")} data-testid="nav-signup" className="rounded-full">Join the tribunal</Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
